import { useEffect, useRef, useState } from 'react';
import { essays } from '@/mocks/homepage';

export default function EssaysSection() {
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="essays" ref={ref} className="bg-cream-50 py-24 md:py-36">
      <div className="w-full px-6 md:px-10 lg:px-16">
        <div className="max-w-5xl mx-auto">
          {/* Header */}
          <div
            className={`flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-16 md:mb-20 transition-all duration-1000 ${
              visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
          >
            <div>
              <p className="text-sage-500 text-xs tracking-[0.3em] uppercase mb-4 font-medium">
                From Our Journals
              </p>
              <h2 className="font-serif text-3xl md:text-5xl text-charcoal-300 font-medium leading-tight">
                Essays that help
                <br />
                <span className="italic font-light">you think deeper.</span>
              </h2>
            </div>
            <button className="text-sage-500 text-sm font-medium hover:text-sage-600 transition-colors whitespace-nowrap flex items-center gap-2">
              View All Essays
              <i className="ri-arrow-right-line" />
            </button>
          </div>

          {/* Essays List */}
          <div className="space-y-0">
            {essays.map((essay, i) => (
              <div
                key={essay.id}
                className={`group border-t border-sand-200 py-8 md:py-10 flex flex-col md:flex-row md:items-start gap-4 md:gap-8 transition-all duration-700 cursor-pointer ${
                  visible
                    ? 'opacity-100 translate-y-0'
                    : 'opacity-0 translate-y-10'
                }`}
                style={{ transitionDelay: `${200 + i * 150}ms` }}
              >
                <div className="flex-shrink-0 w-full md:w-48">
                  <span className="text-sage-400 text-xs tracking-wider uppercase font-medium">
                    {essay.category}
                  </span>
                  <span className="text-sand-400 text-xs ml-3">
                    {essay.readTime}
                  </span>
                </div>
                <div className="flex-1">
                  <h3 className="text-charcoal-300 text-lg md:text-xl font-medium mb-2 group-hover:text-sage-500 transition-colors">
                    {essay.title}
                  </h3>
                  <p className="text-charcoal-100 text-sm md:text-base leading-relaxed">
                    {essay.excerpt}
                  </p>
                </div>
                <div className="flex-shrink-0">
                  <div className="w-8 h-8 rounded-full border border-sand-300 flex items-center justify-center group-hover:border-sage-400 group-hover:bg-sage-50 transition-all">
                    <i className="ri-arrow-right-line text-sand-400 group-hover:text-sage-500 text-sm transition-colors" />
                  </div>
                </div>
              </div>
            ))}
            {/* Bottom border */}
            <div className="border-t border-sand-200" />
          </div>
        </div>
      </div>
    </section>
  );
}